/**
 * ActivityStorageService - Persistence layer for activity progress.
 *
 * Ported from bumblebee ChallengeStorageService. Uses IndexedDB as primary
 * store with localStorage cache. Cloud sync via existing infrastructure.
 *
 * @see GitHub Issue #852
 */

const STORAGE_KEY = 'scratchjr_activity_progress';
const DB_KEY = 'activity_progress';
const PROJECT_LINKS_KEY = 'scratchjr_activity_projects';
const PROJECT_LINKS_DB_KEY = 'activity_projects';
const PROFILE_STORAGE_KEY = 'scratchjr_profile_id';
const DEFAULT_PROFILE_ID = 'default';

export default class ActivityStorageService {
    static _getCurrentProfileId () {
        try {
            return String(
                localStorage.getItem(PROFILE_STORAGE_KEY) ||
                sessionStorage.getItem(PROFILE_STORAGE_KEY) ||
                (window.cloudSyncService && window.cloudSyncService.getStatus && window.cloudSyncService.getStatus().profileId) ||
                DEFAULT_PROFILE_ID
            ).trim() || DEFAULT_PROFILE_ID;
        } catch (_) {
            return DEFAULT_PROFILE_ID;
        }
    }

    static _dbCanRead () {
        return Boolean(
            window.scratchJrDBService &&
            typeof window.scratchJrDBService.get === 'function'
        );
    }

    static _dbCanWrite () {
        return Boolean(
            window.scratchJrDBService &&
            typeof window.scratchJrDBService.set === 'function'
        );
    }

    static _dbCanDelete () {
        return Boolean(
            window.scratchJrDBService &&
            typeof window.scratchJrDBService.delete === 'function'
        );
    }

    static async _readRaw (dbKey, storageKey) {
        try {
            if (ActivityStorageService._dbCanRead()) {
                const data = await window.scratchJrDBService.get(dbKey);
                if (data) return JSON.parse(data);
            }
        } catch (e) {
            // Fall through to localStorage
        }

        try {
            const cached = localStorage.getItem(storageKey);
            if (cached) return JSON.parse(cached);
        } catch (e) {
            // Storage not available
        }

        return null;
    }

    static async _writeRaw (dbKey, storageKey, value) {
        const json = JSON.stringify(value);

        try {
            if (ActivityStorageService._dbCanWrite()) {
                await window.scratchJrDBService.set(dbKey, json);
            }
        } catch (e) {
            console.error(`[ActivityStorageService] IndexedDB save failed for ${dbKey}:`, e);
        }

        try {
            localStorage.setItem(storageKey, json);
        } catch (e) {
            if (e.name === 'QuotaExceededError') {
                console.warn(`[ActivityStorageService] localStorage quota exceeded for ${storageKey}`);
            }
        }
    }

    static _normalizeScopedBucket (raw) {
        if (!raw) {
            return {profiles: {}};
        }

        if (Array.isArray(raw)) {
            return {
                profiles: {
                    [DEFAULT_PROFILE_ID]: raw
                }
            };
        }

        if (typeof raw !== 'object') {
            return {profiles: {}};
        }

        if (raw.profiles && typeof raw.profiles === 'object') {
            return raw;
        }

        // Backward compatibility for the older flat storage shape.
        return {
            profiles: {
                [DEFAULT_PROFILE_ID]: raw
            }
        };
    }

    /**
     * Get all activity progress.
     * @returns {Promise<object>} Map of activityId → progress
     */
    static async getProgress () {
        const scoped = ActivityStorageService._normalizeScopedBucket(
            await ActivityStorageService._readRaw(DB_KEY, STORAGE_KEY)
        );
        const profileId = ActivityStorageService._getCurrentProfileId();
        return scoped.profiles[profileId] || {};
    }

    /**
     * Save progress for a specific activity.
     * @param {string} activityId
     * @param {object} data - {status, stars, attempts, bestScore, hintsUsed, completedAt, timeSpentMs}
     * @returns {Promise<boolean>}
     */
    static async saveProgress (activityId, data) {
        const scoped = ActivityStorageService._normalizeScopedBucket(
            await ActivityStorageService._readRaw(DB_KEY, STORAGE_KEY)
        );
        const profileId = String(data.profileId || ActivityStorageService._getCurrentProfileId()).trim() || DEFAULT_PROFILE_ID;
        const all = scoped.profiles[profileId] || {};
        const attemptDelta = data.attempts !== undefined ? data.attempts : 1;

        // Merge — keep best stars, accumulate attempts
        const existing = all[activityId] || {};
        all[activityId] = {
            ...existing,
            ...data,
            activityId: activityId,
            profileId: profileId,
            status: data.status || existing.status || 'not_started',
            stars: Math.max(data.stars || 0, existing.stars || 0),
            attempts: (existing.attempts || 0) + attemptDelta,
            bestScore: data.stars > (existing.stars || 0) ? data.bestScore : (existing.bestScore || data.bestScore),
            hintsUsed: data.hintsUsed !== undefined ? data.hintsUsed : (existing.hintsUsed || 0),
            completedAt: data.completedAt || existing.completedAt,
            timeSpentMs: (existing.timeSpentMs || 0) + (data.timeSpentMs || 0),
            projectId: data.projectId || existing.projectId,
            activityPath: data.activityPath || existing.activityPath,
            title: data.title || existing.title,
            lastOpenedAt: data.lastOpenedAt || existing.lastOpenedAt
        };
        scoped.profiles[profileId] = all;
        await ActivityStorageService._writeRaw(DB_KEY, STORAGE_KEY, scoped);

        return true;
    }

    /**
     * Get progress for a single activity.
     * @param {string} activityId
     * @returns {Promise<object|null>}
     */
    static async getActivityProgress (activityId) {
        const all = await ActivityStorageService.getProgress();
        return all[activityId] || null;
    }

    /**
     * Get earned badge IDs.
     * @returns {Promise<string[]>}
     */
    static async getEarnedBadges () {
        const key = 'scratchjr_activity_badges';
        const scoped = ActivityStorageService._normalizeScopedBucket(
            await ActivityStorageService._readRaw(key, key)
        );
        const profileId = ActivityStorageService._getCurrentProfileId();
        return scoped.profiles[profileId] || [];
    }

    /**
     * Save newly earned badges.
     * @param {string[]} badgeIds
     * @returns {Promise<boolean>}
     */
    static async saveBadges (badgeIds) {
        const existing = await ActivityStorageService.getEarnedBadges();
        const merged = [...new Set([...existing, ...badgeIds])];
        const key = 'scratchjr_activity_badges';
        const scoped = ActivityStorageService._normalizeScopedBucket(
            await ActivityStorageService._readRaw(key, key)
        );
        const profileId = ActivityStorageService._getCurrentProfileId();
        scoped.profiles[profileId] = merged;
        await ActivityStorageService._writeRaw(key, key, scoped);

        return true;
    }

    /**
     * Get total stars across all activities.
     * @returns {Promise<number>}
     */
    static async getTotalStars () {
        const all = await ActivityStorageService.getProgress();
        return Object.values(all).reduce((sum, p) => sum + (p.stars || 0), 0);
    }

    /**
     * Get completion count for a course.
     * @param {string} courseId
     * @param {string[]} activityIds - All activity IDs in the course
     * @returns {Promise<{completedCount: number, totalCount: number}>}
     */
    static async getCourseProgress (courseId, activityIds) {
        const all = await ActivityStorageService.getProgress();
        const completed = activityIds.filter(id =>
            all[id] && all[id].status === 'completed'
        );
        return {
            completedCount: completed.length,
            totalCount: activityIds.length
        };
    }

    /**
     * Clear all progress (for testing/reset).
     * @returns {Promise<void>}
     */
    static async clearAll () {
        try {
            if (ActivityStorageService._dbCanDelete()) {
                await window.scratchJrDBService.delete(DB_KEY);
                await window.scratchJrDBService.delete('scratchjr_activity_badges');
                await window.scratchJrDBService.delete(PROJECT_LINKS_DB_KEY);
            }
        } catch (e) {
            // Fall through
        }
        try {
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem('scratchjr_activity_badges');
            localStorage.removeItem(PROJECT_LINKS_KEY);
        } catch (e) {
            // Not available
        }
    }

    static async getActivityProjectLinks () {
        const scoped = ActivityStorageService._normalizeScopedBucket(
            await ActivityStorageService._readRaw(PROJECT_LINKS_DB_KEY, PROJECT_LINKS_KEY)
        );
        const profileId = ActivityStorageService._getCurrentProfileId();
        return scoped.profiles[profileId] || {};
    }

    static async getActivityProjectId (activityId) {
        const links = await ActivityStorageService.getActivityProjectLinks();
        if (links[activityId]) {
            return links[activityId];
        }
        const progress = await ActivityStorageService.getActivityProgress(activityId);
        return progress && progress.projectId ? progress.projectId : null;
    }

    static async saveActivityProjectId (activityId, projectId) {
        const scoped = ActivityStorageService._normalizeScopedBucket(
            await ActivityStorageService._readRaw(PROJECT_LINKS_DB_KEY, PROJECT_LINKS_KEY)
        );
        const profileId = ActivityStorageService._getCurrentProfileId();
        const links = scoped.profiles[profileId] || {};
        links[activityId] = projectId;
        scoped.profiles[profileId] = links;
        await ActivityStorageService._writeRaw(PROJECT_LINKS_DB_KEY, PROJECT_LINKS_KEY, scoped);

        return true;
    }

    static async resetActivity (activityId) {
        const profileId = ActivityStorageService._getCurrentProfileId();
        const progressScoped = ActivityStorageService._normalizeScopedBucket(
            await ActivityStorageService._readRaw(DB_KEY, STORAGE_KEY)
        );
        const progressBucket = {...(progressScoped.profiles[profileId] || {})};
        const existingProgress = progressBucket[activityId] || null;
        delete progressBucket[activityId];
        progressScoped.profiles[profileId] = progressBucket;
        await ActivityStorageService._writeRaw(DB_KEY, STORAGE_KEY, progressScoped);

        const linksScoped = ActivityStorageService._normalizeScopedBucket(
            await ActivityStorageService._readRaw(PROJECT_LINKS_DB_KEY, PROJECT_LINKS_KEY)
        );
        const linksBucket = {...(linksScoped.profiles[profileId] || {})};
        const projectId = linksBucket[activityId] || (existingProgress && existingProgress.projectId) || null;
        delete linksBucket[activityId];
        linksScoped.profiles[profileId] = linksBucket;
        await ActivityStorageService._writeRaw(PROJECT_LINKS_DB_KEY, PROJECT_LINKS_KEY, linksScoped);

        return {
            profileId,
            projectId,
            progress: existingProgress
        };
    }
}
